import { Box, Fade, Skeleton, styled, Typography } from '@mui/material';
import { useHover } from 'lib/hooks/useHover';
import { isDefined } from 'lib/typeguards';
import React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { Column } from 'ui/Flex';

interface AudienceTotalsBoxProps {
    mockEnabled?: boolean;
    audienceId?: string;
    onClick?: (audienceId: string) => void;
    color: string;
    title?: string;
    subtitle: string;
    className?: string;
}

const StyledTotalsBackgroundBox = styled(Box)({
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0.1,
    position: 'absolute'
});

const StyledTotalsBox = styled(Box)(({ theme }) => ({
    border: '2px solid #fff',
    padding: theme.spacing(2),
    margin: theme.spacing(1, 0, 1, 2),
    borderRadius: theme.shape.borderRadius,
    flexBasis: '20%',
    position: 'relative',
    minWidth: '100px',
    minHeight: '90px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column'
}));

const StyledTotalsClickable = styled(StyledTotalsBox)(({ theme }) => ({
    cursor: 'pointer',
    position: 'relative',
    transition: 'box-shadow 0.2s ease-in-out',
    '&:hover': {
        boxShadow: theme.shadows[3]
    }
}));

const Subtitle = styled(Typography)({
    maxWidth: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontWeight: 500,
    opacity: 0.75
});

const OverlayBox = styled(Box)(({ theme }) => ({
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    position: 'absolute',
    display: 'flex',
    opacity: 0,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: theme.spacing(1)
}));

export const AudienceTotalsBoxButton = styled(StyledTotalsClickable)(({ theme }) => ({
    border: '2px dashed #fff',
    borderColor: theme.palette.grey[500],
    boxShadow: theme.shadows[0],
    transition: 'box-shadow 0.2s ease-in-out'
}));

export const AudienceTotalsBox: React.FC<AudienceTotalsBoxProps> = ({
    audienceId,
    title,
    subtitle,
    color,
    onClick,
    className
}) => {
    const handleClick = React.useCallback(() => {
        if (onClick && isDefined(audienceId)) {
            onClick(audienceId);
        }
    }, [audienceId, onClick]);
    const [ref, isHovering] = useHover();
    const Component = React.useMemo(() => (onClick ? StyledTotalsClickable : StyledTotalsBox), [onClick]);
    if (!isDefined(title)) {
        return (
            <StyledTotalsBox sx={{ borderColor: color }}>
                <StyledTotalsBackgroundBox sx={{ backgroundColor: color }} />
                <Typography>Loading</Typography>
            </StyledTotalsBox>
        );
    }
    return (
        <Component
            onClick={handleClick}
            sx={{ borderColor: color, fontWeight: 500 }}
            className={className}
            ref={ref}
        >
            <StyledTotalsBackgroundBox sx={{ backgroundColor: color }} />
            <Typography variant="h6" align="center" sx={{ fontWeight: 'bold', opacity: 0.9 }}>
                {title}
            </Typography>
            <Subtitle align="center" variant="body2">
                {subtitle}
            </Subtitle>
            <Fade in={isHovering && !!onClick}>
                <OverlayBox>
                    <StyledTotalsBackgroundBox sx={{ backgroundColor: color }} />
                    <Column align="center">
                        <EditIcon sx={{ fontSize: '2rem' }} />
                        <Typography variant="caption">Edit audiences</Typography>
                    </Column>
                </OverlayBox>
            </Fade>
        </Component>
    );
};

interface AudienceTotalsBoxSkeletonProps {
    color?: string;
    subtitle?: string;
    className?: string;
}

export const AudienceTotalsBoxSkeleton: React.FC<AudienceTotalsBoxSkeletonProps> = ({
    color,
    subtitle,
    className
}) => (
    <StyledTotalsBox sx={{ borderColor: color }} className={className}>
        <StyledTotalsBackgroundBox sx={{ backgroundColor: color }} />
        <Skeleton variant="text" width={100} height={29} />
        {subtitle ? (
            <Subtitle align="center" variant="body2">
                {subtitle}
            </Subtitle>
        ) : (
            <Skeleton variant="text" width={100} height={20} />
        )}
    </StyledTotalsBox>
);
